import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Link2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRole } from "@/hooks/auth";
import { useDocumentContent } from "@/hooks/documents";
import { useUpdateDocumentContent } from "@/hooks/documents/useDocumentMutations";
import { useDocumentParsingStatus } from "@/hooks/documents/useDocumentParsingStatus";
import { PDFService } from "@/services/knowledgebase/PDFService";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { formatFileSize } from "@/utils/formatting/general";
import { AgentAssociationDisplay } from "../AgentAssociationDisplay";
import { DocumentContentEditor } from "./DocumentContentEditor";
import { DocumentUnderstanding } from "./DocumentUnderstanding";
import { ShareEditLinkPanel } from "./ShareEditLinkPanel";
import { ActionButtons, ContentViewer, DocumentHeader, DocumentMetadata, PaginationControls } from "./index";

interface DocumentDetailsDrawerProps {
  document: DocumentWithMetadata | null;
  isOpen: boolean;
  onClose: () => void;
  onSendToKB: (document: DocumentWithMetadata) => void;
  onRemoveFromKB: (document: DocumentWithMetadata) => void;
  onDocumentUpdate?: (document: DocumentWithMetadata, updates: Partial<DocumentWithMetadata>) => Promise<unknown>;
  onDocumentDelete?: (id: string) => void;
  readOnly?: boolean;
}

const DocumentDetailsDrawerContent = ({
  document,
  isOpen,
  onClose,
  onSendToKB,
  onRemoveFromKB,
  onDocumentUpdate,
  onDocumentDelete,
  readOnly,
}: DocumentDetailsDrawerProps & { document: DocumentWithMetadata }) => {
  const getPortalContainer = () => {
    if (typeof window === "undefined") return null;

    // When opened from inside Radix Sheet/Dialog, render within the active dialog
    // content so pointer-events/scroll locking from the parent layer doesn't block interaction.
    const openDialogContents = Array.from(
      window.document.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"]'),
    );

    return openDialogContents.at(-1) ?? window.document.body;
  };

  const isPdf = PDFService.isPdf(document);

  const {
    viewMode,
    setViewMode,
    urlContent,
    setUrlContent,
    optimizedContent,
    setOptimizedContent,
    totalPages,
    totalParts,
    currentPart,
    setCurrentPart,
    isLoading,
    isOptimizing,
    hasMetadataJson,
    contentPage,
    setContentPage,
    getCurrentPageContent,
    getTotalPages,
    needsPagination,
    refreshContent,
    fixDocument,
    loadOptimizedContent,
    isProcessing: hookIsProcessing,
    processingProgress: hookProcessingProgress,
  } = useDocumentContent(document, isOpen);

  const { documentParsingStatus, retryParsing, retryingDocuments } = useDocumentParsingStatus([document]);
  const { isInternalAdmin } = useRole();
  const updateDocumentContentMutation = useUpdateDocumentContent();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isUpdatingContent, setIsUpdatingContent] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRawTextExpanded, setIsRawTextExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) setIsEditing(false);
  }, [isOpen, document?.id]);

  // Collapse the raw-text disclosure whenever the drawer closes or a different
  // document is opened; while the drawer stays open on the same document, keep
  // whatever the user chose.
  useEffect(() => {
    if (!isOpen) setIsRawTextExpanded(false);
  }, [isOpen, document?.id]);

  // Refresh content when document properties change (e.g., optimized_path after optimization completes)
  // or when parsing status indicates completion
  useEffect(() => {
    if (!isOpen || !document) return;

    const parsingStatus = documentParsingStatus[document.id];

    // Refresh when:
    // 1. Parsing completes
    // 2. Document properties change (via refetch after realtime events)
    if (parsingStatus?.isCompleted || document.optimized_path || document.parsed_path || document.el_doc_id) {
      const timer = setTimeout(() => {
        refreshContent(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [document, isOpen, documentParsingStatus, refreshContent]);

  const handleRetryParse = useCallback(async () => {
    if (!document) return;
    await retryParsing(document);
    setTimeout(() => {
      if (isOpen) {
        refreshContent();
      }
    }, 2000);
  }, [document, retryParsing, isOpen, refreshContent]);

  const handleDeleteDocument = useCallback(() => {
    if (!document || !onDocumentDelete) return;

    onDocumentDelete(document.id);
    onClose();
  }, [document, onDocumentDelete, onClose]);

  const handleOptimizeDocument = useCallback(
    async (fullDocument: boolean) => {
      await fixDocument(fullDocument, onDocumentUpdate);
    },
    [fixDocument, onDocumentUpdate],
  );

  const handleContentUpdate = useCallback(
    async (content: string, isOptimized: boolean) => {
      if (!document) return;

      setIsUpdatingContent(true);
      try {
        await updateDocumentContentMutation.mutateAsync({
          documentId: document.id,
          content,
          isOptimized,
        });

        if (isOptimized) {
          setOptimizedContent(content);
        } else {
          setUrlContent(content);
        }
      } catch (error) {
        console.error("Content update failed:", error);
      } finally {
        setIsUpdatingContent(false);
      }
    },
    [document, updateDocumentContentMutation, setOptimizedContent, setUrlContent],
  );

  const handleOpenEditor = useCallback(() => {
    setIsEditorOpen(true);
  }, []);

  const handleCloseEditor = useCallback(() => {
    setIsEditorOpen(false);
  }, []);

  const currentContent = viewMode === "optimized" ? optimizedContent : urlContent;

  // Get realtime progress from parsing status hook (priority) or fall back to hook's polling
  const parsingStatus = documentParsingStatus[document.id];
  const realtimeProgress = parsingStatus?.progress ?? null;

  // Use realtime progress for PDFs when available, otherwise use hook's polling progress
  const correctProgress = isPdf && realtimeProgress !== null ? realtimeProgress : hookProcessingProgress;

  // For PDFs: Use realtime progress status (instant) if available, otherwise use hook's polling status
  // For plain text: Use metadata status
  const correctIsProcessing = isPdf
    ? realtimeProgress !== null
      ? !parsingStatus?.isCompleted
      : hookIsProcessing
    : document?.status === "in_progress";

  const documentSize = (() => {
    if (document?.metadata?.pdf_size || document.metadata?.size) {
      return formatFileSize(document.metadata.pdf_size || document.metadata?.size);
    }
    if (currentContent) {
      return formatFileSize(currentContent.length);
    }
    return "Unknown";
  })();

  const totalContentPages = getTotalPages(currentContent);
  const calculatedTotalPages = totalPages || totalContentPages;
  const isOptimizationFailed = document.metadata?.optimization_failed === true;
  const isAlreadyOptimized =
    Boolean(document.optimized_path) ||
    document.metadata?.optimized === true ||
    document.metadata?.optimized_file_path ||
    !!optimizedContent;
  const hasRawContent = hasMetadataJson || urlContent;

  const isDocumentOlderThan30Minutes =
    document.created_at && new Date(Date.now() - 30 * 60 * 1000) > new Date(document.created_at);
  const canOptimize = !document.el_doc_id && !isAlreadyOptimized && hasRawContent && isDocumentOlderThan30Minutes;
  const isNonPdfParsing = !isPdf && correctIsProcessing;

  if (!isOpen) return null;

  const drawerContent = (
    <>
      <div className="fixed inset-0 bg-black/50 z-[100]" onClick={onClose} />
      <div
        className="fixed right-0 top-0 h-full w-full sm:w-[70vw] bg-white z-[100] flex flex-col overflow-hidden transform transition-transform duration-300 ease-in-out"
        style={{
          boxShadow: "-8px 0 24px -4px rgba(79, 156, 255, 0.15), -4px 0 12px -2px rgba(79, 156, 255, 0.1)",
        }}
      >
        <DocumentHeader
          document={document}
          onClose={onClose}
          onDocumentUpdate={onDocumentUpdate}
          isEditing={isEditing && !readOnly}
          onToggleEdit={() => setIsEditing((v) => !v)}
          readOnly={readOnly}
        />

        {/* Single scroll owner for everything between the pinned header and the pinned action bar.
            Nothing below this line should carry its own flex-1/min-h-0 sizing — they just stack
            in normal flow and this container scrolls when they overflow the drawer's height. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <DocumentMetadata
            document={document}
            documentSize={documentSize}
            totalPages={calculatedTotalPages}
            onDocumentUpdate={onDocumentUpdate}
            documentParsingStatus={documentParsingStatus}
            isEditing={isEditing && !readOnly}
          />

          {document.el_doc_id && (
            <div className="mx-6 mt-4 flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-1/2 rounded-xl border border-[#E5EAF0] bg-white p-4">
                <AgentAssociationDisplay
                  dependentAgents={document.dependent_agents || []}
                  size="md"
                  maxDisplay={8}
                  mode="labeled-avatars"
                  showLabel={false}
                />
              </div>

              <div
                className={cn(
                  "w-full sm:w-1/2 rounded-xl border bg-white overflow-hidden transition-all",
                  isShareOpen ? "border-genie-primary/40 shadow-sm" : "border-[#E5EAF0]",
                )}
              >
                <button
                  onClick={() => setIsShareOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F9FAFC]"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-genie-primary/10">
                      <Link2 className="h-4 w-4 text-genie-primary" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-gray-800">Share an edit link</span>
                      <span className="block text-xs text-[#64748B]">
                        Let someone update this daily without logging in
                      </span>
                    </span>
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-genie-primary">
                    {isShareOpen ? "Hide" : "Manage"}
                    <ChevronDown className={cn("h-4 w-4 transition-transform", isShareOpen && "rotate-180")} />
                  </span>
                </button>
                {isShareOpen && (
                  <div className="border-t border-[#E5EAF0] p-4 max-h-[45vh] overflow-y-auto">
                    <ShareEditLinkPanel document={document} isOpen={isShareOpen} />
                  </div>
                )}
              </div>
            </div>
          )}

          {isNonPdfParsing && (
            <div className="mx-6 mt-4 mb-2">
              <Alert className="border-2 border-genie-secondary/40 bg-gradient-to-r from-genie-secondary/10 to-genie-secondary/5 text-genie-text rounded-xl shadow-md">
                <Loader2 className="h-5 w-5 text-genie-secondary animate-spin" />
                <AlertDescription>
                  <div className="text-sm">
                    <strong className="font-semibold">Parsing In Progress:</strong> This document is currently being
                    parsed. Please check back later.
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DocumentUnderstanding document={document} onDocumentUpdate={onDocumentUpdate} readOnly={readOnly} />

          <div className="mt-4 border-t border-[#E5EAF0]">
            <button
              type="button"
              onClick={() => setIsRawTextExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-6 py-3 text-left transition-colors hover:bg-[#F9FAFC]"
            >
              <span className="text-sm font-semibold text-gray-800">Read the full source</span>
              <span className="flex items-center gap-1.5 text-xs font-medium text-genie-primary">
                {isRawTextExpanded ? "Hide" : "Show"}
                <ChevronDown className={cn("h-4 w-4 transition-transform", isRawTextExpanded && "rotate-180")} />
              </span>
            </button>

            {isRawTextExpanded && (
              <div className="border-t border-[#E5EAF0]">
                {/* ContentViewer is written to flex to fill a bounded ancestor (flex-1 min-h-0
                    internally). Now that it sits inside a scrolling parent rather than a fixed-
                    height flex box, it needs an explicit height to flex against, or its internal
                    overflow-y-auto has nothing to bound itself to. This box also intentionally
                    stops short of the drawer's full height so the toolbar/pager below it — and the
                    scrollbar on the outer container — stay reachable instead of trapping the wheel. */}
                <div className="h-[50vh] flex flex-col overflow-hidden">
                  <ContentViewer
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    urlContent={urlContent}
                    optimizedContent={optimizedContent}
                    isLoading={isLoading}
                    isOptimizing={isOptimizing}
                    isProcessing={isPdf ? correctIsProcessing : false}
                    processingProgress={correctProgress}
                    getCurrentPageContent={getCurrentPageContent}
                    onRefreshContent={refreshContent}
                    loadOptimizedContent={loadOptimizedContent}
                    onOpenEditor={handleOpenEditor}
                    onViewModeChange={() => setCurrentPart(1)}
                    currentPage={contentPage}
                    currentPart={currentPart}
                  />
                </div>

                <PaginationControls
                  contentPage={contentPage}
                  setContentPage={setContentPage}
                  totalContentPages={totalContentPages}
                  needsPagination={needsPagination}
                  currentContent={currentContent}
                  totalParts={totalParts}
                  currentPart={currentPart}
                  setCurrentPart={setCurrentPart}
                />
              </div>
            )}
          </div>
        </div>

        {!readOnly && (
          <ActionButtons
            document={document}
            hasMetadataJson={hasMetadataJson}
            urlContent={urlContent}
            optimizedContent={optimizedContent}
            isOptimizing={isOptimizing}
            isProcessing={correctIsProcessing}
            isInternalAdmin={isInternalAdmin()}
            retryingDocuments={retryingDocuments}
            viewMode={viewMode}
            canOptimize={canOptimize}
            isAlreadyOptimized={isAlreadyOptimized}
            onDeleteDocument={handleDeleteDocument}
            onRetryParse={handleRetryParse}
            onOptimizeDocument={handleOptimizeDocument}
            onSendToKB={onSendToKB}
            onRemoveFromKB={onRemoveFromKB}
            onClose={onClose}
          />
        )}
      </div>

      {/* Content Editor Modal */}
      <DocumentContentEditor
        document={document}
        currentContent={viewMode === "optimized" ? optimizedContent : urlContent}
        isOptimized={viewMode === "optimized"}
        isOpen={isEditorOpen}
        onClose={handleCloseEditor}
        onSave={handleContentUpdate}
        isUpdating={isUpdatingContent}
      />
    </>
  );

  const portalContainer = getPortalContainer();
  return portalContainer ? createPortal(drawerContent, portalContainer) : null;
};

const DocumentDetailsDrawer = (props: DocumentDetailsDrawerProps) => {
  if (!props.document) return null;
  return <DocumentDetailsDrawerContent {...props} document={props.document} />;
};

export default DocumentDetailsDrawer;
